import { ICONS, Icon } from "@/modules/ui-elements/Icon";
import { Button, Dialog } from "@mui/material";
import styles from './styles.module.css';
import SearchBox from "@/components/searchBox/searchBox";
import ProjectList from "./ProjectList";
import { getPaginatedJobs } from "@/modules/editor-v2/services/MyWorks";
import { useInfiniteQuery } from "@tanstack/react-query";
import { compact, debounce } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import Loader from "@/modules/ui-elements/Loader";
import { uploadExistingImagesToTemp } from "@/modules/editor-v2/services/Listing";
import { useRouter } from "next/router";
import { useAtom, useSetAtom } from "jotai";
import { imagesForLoadingAtom, imagesURLsAtom, triggerJobGroupCreationAtom } from "@/modules/marketplaceListing/Atoms";
import useAuth from "@/hooks/useAuth";

export default function ExistingProjectsListPopup (props) {
    const { user } = useAuth();
    const [searchText, setSearchText] = useState("");
    const [loadOnProjectSelect, setLoadOnProjectSelect] = useState(false);
    const setTriggerForJobCreation = useSetAtom<any>(triggerJobGroupCreationAtom);
    const setLoadingImages = useSetAtom<any>(imagesForLoadingAtom);
    const [selectedProject,setSelectedProject] = useState(null);
    const [selectedImage,setSelectedImage] = useState([]);
    const [imagesUrls, setImagesUrls] = useAtom(imagesURLsAtom)
    const isExternalUser = useMemo(()=>props?.isBulkAIType && !user?.user?.infoGraphicsUser,[props?.isBulkAIType,user]);
    const router = useRouter();
    const {status,  data, isFetchingNextPage, hasNextPage, fetchNextPage, isLoading} = useInfiniteQuery({
        queryKey: ["projectList", "infinite", searchText],
        getNextPageParam: (lastPage:any[], pages) =>(lastPage?.length >= 10) ? pages.length : undefined,
        queryFn: ({pageParam}) =>getPaginatedJobs({pageParam, searchText, selectedTab: "COMPLETED"}),
    })

    const buffer = useRef(50);
    const onScroll = (e) => {
        if(isFetchingNextPage) return;
        if (e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - buffer.current) {
            fetchNextPage();
        }
    };

    const debounceSearch = debounce((text:string) => {
        setSearchText(text);
    }, 600)

    const onSearchInputChange = (text:string)=>{
        debounceSearch(text);
    };

    const handleBack = () => {
        setSelectedProject(null);
        setSelectedImage(null);
    };

    const handleNext = () => {
        if(selectedImage?.length < 1)return;
        const payload = [{
            imageUrls: selectedImage?.map((_)=>({dataURL:_})),
            name: selectedProject?.name || "ImageAI",
          }];
          setSelectedProject(null);
          setSelectedImage(null);
          props?.onExistingProjectHandler(payload);
    };

    const onSelectHandler = (item) => {
        const alreadyExist = selectedImage?.includes(item);
        if(alreadyExist){
            let imageList = JSON.parse(JSON.stringify(selectedImage));
            imageList = selectedImage?.filter(e=>e !== item);
            setSelectedImage(imageList);
            return;
        }
        if(isExternalUser)setSelectedImage([item]);
        else setSelectedImage(prev=>([...(prev || []),item]));
    };

    const onProjectSelect = async (selected) => {
        if(props?.isBulkAIType) {
            setSelectedProject(selected);
            if (selected?.images?.length === 1) {
                setSelectedImage([selected.images[0].deliveryLink]);
            }
            return;
        }
        if (selected?.aiTaskDisable) return;
        setLoadOnProjectSelect(true)
        if(props?.homePageBatchEditorFlow ) //for batch editor jobsusing project URLs
        {
            setLoadOnProjectSelect(false)
            const imagesUrls = selected?.images?.map((image)=> image?.dmsPNG);
            const ExistingProjectZerothJobName = selected?.images?.[0]?.angle_name;
            props?.batchEditorJobFromExistingImages(imagesUrls, ExistingProjectZerothJobName);
            return
        }
        const imagesIdArr = selected?.pages?.map((page)=> page?.image_id)
        const res = await uploadExistingImagesToTemp(imagesIdArr)
        setImagesUrls(res?.data)

        if(res?.success){
            setTriggerForJobCreation(
                {
                    selectedJob:{...selected}, 
                    images:res?.data, 
                    marketplace_id:"64cb7e5423d9644b4cf689e2"
                }
            )
            setLoadingImages({images:res?.data});
            setLoadOnProjectSelect(false)
            if(props.onlyUploadImages){
                props.backHandler();
                props.setOpenFileUploader?.(false)
                return;
            }
            router.push(`/dashboard/marketplace-listing`)
        }
    }

    return(
        <Dialog 
            open={props?.openExistingProjects} 
            maxWidth="xl"
            scroll="paper"
            aria-labelledby="customized-dialog-title"
            PaperProps={{
                sx: { backgroundColor: "white", display: 'flex', flexDirection: 'column' },
                style: { borderRadius: '4px', width: "650px", height: "480px" }
            }}
        >
        {selectedProject ? (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <div className={styles.header} style={{ paddingBottom: '7px' }}>
                    <div className={styles.projectHeadingContainer}>
                        <div className={styles.projectNameContainer}>
                            <div className={styles.headerIcon} onClick={handleBack}>
                                <Icon src={ICONS.chevronLeftDark} height={16} width={16} />
                            </div>
                            <div className={styles.projectsHeading}>
                                {selectedProject?.name || "Project"}
                            </div>
                        </div>
                        <div className={styles.projectSubHeading}>
                            {props?.webListingScreen?.SelectedProjectSubheading || "Pick an image and click Next"}
                        </div>
                    </div>
                </div>

                {/* Scrollable Image Grid */}
                <div style={{ flex: 1, display: 'flex', flexWrap: "wrap", gap: '2%', justifyContent: 'flex-start', padding: '10px', overflowY: 'auto' }}>
                    {
                        selectedProject?.images?.map((item) => (
                            <div
                                key={item?.deliveryLink}
                                style={{
                                    width: '32%',
                                    aspectRatio: 1,
                                    maxHeight: '300px',
                                    border: `1px solid ${selectedImage?.includes(item?.deliveryLink) ? "#000" : "#eee"}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => onSelectHandler(item?.deliveryLink)}
                            >
                                <img
                                    src={item?.deliveryLink}
                                    alt="img"
                                    style={{ objectFit: 'contain', width: '100%', height: '100%', borderRadius: '8px' }}
                                />
                            </div>
                        ))
                    }
                </div>

                {/* Fixed Footer Button */}
                <div style={{ borderTop: '1px solid #eee', padding: '10px' }}>
                    <Button
                        sx={{
                            border: '1px solid #262626',
                            width: '100%',
                            background: '#262626',
                            color: '#fff',
                            borderRadius: '4px',
                            ":hover": { backgroundColor: '#262626', color: '#fff' }
                        }}
                        disabled={!selectedImage?.length}
                        onClick={handleNext}
                    >
                        Next
                    </Button>
                </div>
            </div>
        ) : (
            // Unchanged code for the non-selectedProject state
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className={styles.header}>
                    <div className={styles.headerIcon} onClick={props.backHandler}>
                        <Icon src={ICONS.chevronLeftDark} height={16} width={16} />
                    </div>
                    <div className={styles.projectsHeading}>
                        {props?.webListingScreen?.YourProjectsTitle || "Your Projects"}
                    </div>
                </div>
                <div className={styles.searchBoxContainer}>
                    {/* @ts-ignore */}
                    <SearchBox onChange={onSearchInputChange}/>
                </div>
                <div
                    className={styles.projectListContainer}
                    style={{ flex: 1, overflowY: 'auto' }}
                    onScroll={onScroll}
                >
                    {isLoading || loadOnProjectSelect ? (
                        <div style={{ height: 300, width: 550, alignSelf: 'center' }}>
                            <Loader height={16} width={16}/>
                        </div>
                    ) : !data?.pages?.length || (data?.pages?.length === 1 && !data?.pages?.[0]) ? (
                        <div style={{ textAlign: 'center' }}>
                            {props?.webListingScreen?.NoProjectsText || "No Projects Yet"}
                        </div>
                    ) : (
                        compact(data?.pages.flatMap(x => x))
                            .filter(item => !item?.aiTaskDisable)
                            .map(item => <ProjectList key={item.id} item={item} onProjectSelect={onProjectSelect}/>)
                    )}
                    {isFetchingNextPage && !loadOnProjectSelect && !isLoading && (
                        <div className={styles.messageContainer}>Loading....</div>
                    )}
                </div>
            </div>
        )}
        </Dialog>
    )
}